import {DownloadOutlined} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Menu,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {format, parseISO} from 'date-fns';
import {useEffect, useRef, useState} from 'react';
import {toast} from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQGetAllUserOrders,
  RQGetCategoryAPI,
  RQGetOrderByFilter,
  RQOrderRegistration,
  RqUpdateOrder,
} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';
import InvoiceScreen from '../../Order/InvoiceScreen/InvoiceScreen';

const UserHistory = () => {
  const [products, setProducts] = useState(null);

  const [dataSource, setDataSource] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState(null);

  const [pageSize, setPageSize] = useState(10);

  const [editingCell, setEditingCell] = useState({key: null, field: null});

  const [editedStatus, setEditedStatus] = useState(null);

  const [selectedOrderSummary, setSelectedOrderSummary] = useState(null);

  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const {data: userData, refetch} = RQGetAllUserOrders(token);

  const {Option} = Select;

  const data = dataSource;

  const {mutateAsync: registerOrder} = RQOrderRegistration(token);

  const pageSizeOptions = ['10', '20', '30', '50'];

  const updateOrderMutation = RqUpdateOrder();
  const {data: categoryList = []} = RQGetCategoryAPI(token);

  const registerOrderFromRecord = async record => {
    try {
      const productsPayload = (record.products || []).map(p => ({
        productId: p.productId,
        quantity: p.quantity,
        size: p.size ?? null,
        color: p.color || null,
      }));

      const orderItems = (record.products || []).map(p => ({
        name: p.productInfo?.productName,
        sku: p.productId,
        units: p.quantity,
        selling_price: p.productInfo?.price,
      }));

      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(
        now.getHours(),
      ).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      const isCOD =
        record.paymentMethod === 'COD' ||
        record.paymentMethod === 'CASH_ON_DELIVERY';

      const basePayload = {
        userId: record.userInfo?.id,
        products: productsPayload,
        orderId: `${isCOD ? 'COD' : 'ONLINE'}-${Date.now()}`,
        orderDate: formattedDate,
        pickupLocation: 'Primary Warehouse',

        billingCustomerName: record.userInfo?.firstName,
        billingLastName: record.userInfo?.lastName,
        billingAddress: record.userInfo?.address,
        billingCity: record.userInfo?.city,
        billingPincode: record.userInfo?.pinCode,
        billingState: record.userInfo?.state,
        billingCountry: 'India',
        billingEmail: record.userInfo?.email,
        billingPhone: record.userInfo?.mobile,

        shippingIsBilling: true,
        orderItems,

        length: 30,
        breadth: 11,
        height: 25,
        weight: 0.5,
      };

      const payload = isCOD
        ? {
          ...basePayload,
          paymentMethod: 'COD',
          subTotal: Math.max(record.totalAmount || 1, 1),
          collectableAmount: Math.max(record.totalAmount || 1, 1),
          transactionId: `COD-${Date.now()}`,
        }
        : {
          ...basePayload,
          paymentMethod: 'Prepaid',
          subTotal: record.totalAmount,
          transactionId: record.transactionId,
        };

      console.log('REGISTER ORDER PAYLOAD:', payload);

      const response = await registerOrder(payload);
      return response;
    } catch (error) {
      console.error('Register order error:', error);
      throw error;
    }
  };

  const toDropboxRawUrl = (url = '') =>
    url
      .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');

  const expandColumns = [
    {
      title: 'Product Image',
      dataIndex: 'images',
      key: 'images',
      render: url =>
        url ? (
          <img
            src={toDropboxRawUrl(url)}
            alt={'Product'}
            style={{
              width: 60,
              height: 60,
              objectFit: 'cover',
              borderRadius: 4,
              border: '1px solid #ddd',
            }}
          />
        ) : (
          <Tag color={'default'}>No Image</Tag>
        ),
    },
    {
      title: 'Base Price',
      dataIndex: 'basePrice',
      key: 'basePrice',
      render: price => `₹ ${price.toFixed(2)}`,
    },

    {
      title: 'Discount %',
      dataIndex: 'discountPercent',
      key: 'discountPercent',
      render: percent => (percent > 0 ? `${percent}%` : '—'),
    },

    {
      title: 'Discounted Price',
      dataIndex: 'discountedPrice',
      key: 'discountedPrice',
      render: price => `₹ ${price.toFixed(2)}`,
    },
    {
      title: 'Size & Qty',
      key: 'sizes',
      dataIndex: 'size',
      render: sizeArray =>
        Array.isArray(sizeArray) ? (
          <Space direction={'vertical'}>
            {sizeArray.map((item, index) => (
              <Tag key={index} color={'blue'}>
                Size: {item.size}, Qty: {item.quantity}
              </Tag>
            ))}
          </Space>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: value => `₹ ${Number(value || 0).toFixed(2)}`,
    },
  ];

  const uniqueUsers = userData
    ? [
      ...new Map(
        userData.map(user => [user.userInfo?.id, user.userInfo]),
      ).values(),
    ]
    : [];

  const uniqueProducts = userData
    ? [
      ...new Map(
        userData
          .flatMap(order => order.products || [])
          .map(p => [p?.productId, p]),
      ).values(),
    ]
    : [];

  const filterConfig = {};
  if (selectedUser) {
    filterConfig.userId = selectedUser;
  }
  if (products) {
    filterConfig.productId = products;
  }
  if (selectedStatus) {
    filterConfig.status = selectedStatus;
  }

  const {data: filterdOrderData} = RQGetOrderByFilter(token, filterConfig);

  const isFilterApplied = selectedUser || products || selectedStatus;

  const normalizedFilteredData = filterdOrderData
    ? Array.isArray(filterdOrderData)
      ? filterdOrderData
      : [filterdOrderData]
    : [];

  const tableData = isFilterApplied ? normalizedFilteredData : dataSource;

  const paginatedData = tableData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handleResetFilters = () => {
    setSelectedUser(null);
    setProducts(null);
    setSelectedStatus(null);
  };

  const handleSave = async (id, field, newValue) => {
    try {
      const record =
      dataSource.find(row => row.id === id) ||
      normalizedFilteredData.find(row => row.id === id);

      const payload = {
        ...record,
        [field]: newValue,
      };

      await updateOrderMutation.mutateAsync({
        token,
        id,
        formData: payload,
      });

      await refetch();

      // dynamic success message
      const successMessage =
      field === 'weight'
        ? 'Weight updated successfully'
        : field === 'status'
          ? `Order status updated to ${newValue}`
          : 'Updated successfully';

      toast.success(successMessage);

      // update table locally
      setDataSource(prev =>
        prev.map(row =>
          row.id === id
            ? {...row, [field]: newValue}
            : row,
        ),
      );

    } catch (err) {
      console.error(err);

      toast.error(
        field === 'weight'
          ? 'Failed to update weight'
          : field === 'status'
            ? 'Failed to update order status'
            : 'Update failed',
      );
    }
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (!userData) {
      return;
    }

    const ordersArray = Array.isArray(userData) ? userData : [userData];

    const updated = ordersArray.map(order => ({
      ...order,
      key: order.id || order.transactionId,
      weight: order.weight || 0,
      totalQuantity:
        order.totalQuantity ??
        order.products?.reduce((sum, p) => sum + (p.quantity || 0), 0),
    }));

    const sortedByLatest = [...updated].sort(
      (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
    );

    setDataSource(sortedByLatest);
  }, [userData]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingCell]);

  const expandedRowRender = record => {
    const products = (record.products || []).map(product => {
      const basePrice = Number(product.productInfo?.price || 0);
      const discountPercent = Number(product.productInfo?.discount || 0);

      const discountedPrice =
      discountPercent > 0
        ? basePrice - (basePrice * discountPercent) / 100
        : basePrice;

      const quantity = product.quantity || 0;

      return {
        key: `${record.id}-${product.productId}`,
        images:
        product.productInfo?.imageUrl?.[0] ||
        product.productInfo?.variants?.[0]?.images?.[0] ||
        '',
        productName: product.productInfo?.productName || '-',
        productType: product.productInfo?.productType || '-',
        basePrice,
        discountPercent,
        discountedPrice,
        quantity,
        subtotal: discountedPrice * quantity, // ✅ ADD THIS
        size: [
          {
            size: product.size,
            quantity,
          },
        ],
      };
    });

    return (
      <Table
        columns={expandColumns}
        dataSource={products}
        pagination={false}
        rowKey={'key'}
        size={'small'}
      />
    );
  };

  const columns = [
    {
      title: 'No.',
      key: 'index',
      width: 30,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'User',
      key: 'user',
      width: 100,
      render: (_, record) => {
        const user = record.userInfo;
        return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
      },
    },
    {
      title: 'Transaction Id',
      dataIndex: 'transactionId',
      key: 'transactionId',
      width: 100,
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight',
      key: 'weight',
      width: 120,

      render: (text, record) => {
        const isEditing =
          editingCell.key === record.id && editingCell.field === 'weight';

        // INPUT MODE
        if (isEditing) {
          return (
            <input
              ref={inputRef}
              type={'number'}
              step={'0.01'}
              min={'0'}
              defaultValue={text ?? 0}
              className={`
            border
            border-gray-300
            rounded
            px-2
            py-1
            w-24
            focus:outline-none
            focus:border-blue-500
          `}
              onBlur={e => {
                const newValue = Number(e.target.value);

                // close editing mode
                setEditingCell({
                  key: null,
                  field: null,
                });

                // validation
                if (isNaN(newValue) || newValue <= 0) {
                  toast.error('Weight must be greater than 0');

                  return;
                }

                // update only if changed
                if (newValue !== text) {
                  handleSave(record.id, 'weight', newValue);
                }
              }}
              onKeyDown={e => {
                // save on enter
                if (e.key === 'Enter') {
                  e.target.blur();
                }

                // cancel on escape
                if (e.key === 'Escape') {
                  setEditingCell({
                    key: null,
                    field: null,
                  });
                }
              }}
            />
          );
        }

        // DISPLAY MODE
        return (
          <button
            type={'button'}
            className={`
          cursor-pointer
          hover:text-blue-600
          bg-transparent
          border-none
          p-0`}
        
            onClick={() => {
              setEditingCell({
                key: record.id,
                field: 'weight',
              });
            }}
          >
            {text ?? 0} kg
          </button>
        );
      },
    },
    {
      title: 'Total Qty',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 100,
    },
    {
      title: 'Promo Code Applied',
      dataIndex: 'appliedPromoCode',
      key: 'appliedPromoCode',
      width: 150,
      render: appliedPromoCode => {
        if (!appliedPromoCode) {
          return '-';
        }
        return appliedPromoCode;
      },
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 160,
      render: paymentMethod => {
        const method = (paymentMethod || '').toUpperCase();

        if (method === 'COD' || method === 'CASH_ON_DELIVERY') {
          return 'Cash On Delivery';
        }

        if (method === 'ONLINE' || method === 'RAZORPAY') {
          return 'Online Payment (Razorpay)';
        }

        return paymentMethod || '-';
      },
    },

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (text, record) => {
        const handleStatusChange = async ({key}) => {
          const newStatus = key;

          if (newStatus === record.status) {return;}

          await handleSave(record.id, 'status', newStatus);
        };

        return (
          <Dropdown
            overlay={(
              <Menu onClick={handleStatusChange}>
                {record.status !== 'CONFIRMED' && (
                  <Menu.Item key={'CONFIRMED'}>
                    <Tag color={'green'}>CONFIRMED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'PENDING' && (
                  <Menu.Item key={'PENDING'}>
                    <Tag color={'orange'}>PENDING</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'CANCELLED' && (
                  <Menu.Item key={'CANCELLED'}>
                    <Tag color={'red'}>CANCELLED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'SHIPPED' && (
                  <Menu.Item key={'SHIPPED'}>
                    <Tag color={'blue'}>SHIPPED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'DELIVERED' && (
                  <Menu.Item key={'DELIVERED'}>
                    <Tag color={'purple'}>DELIVERED</Tag>
                  </Menu.Item>
                )}
              </Menu>
            )}
            trigger={['click']}
          >
            <div className={'cursor-pointer w-fit'}>
              <Tag
                color={
                  text === 'CONFIRMED'
                    ? 'green'
                    : text === 'PENDING'
                      ? 'orange'
                      : text === 'CANCELLED'
                        ? 'red'
                        : text === 'SHIPPED'
                          ? 'blue'
                          : text === 'DELIVERED'
                            ? 'purple'
                            : 'default'
                }
              >
                {text} <span> &#9662;</span>
              </Tag>
            </div>
          </Dropdown>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 100,
      render: date => format(parseISO(date), 'dd/MM/yyyy HH:mm'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title={'Show Invoice'}>
          <Button
            type={'link'}
            icon={<DownloadOutlined />}
            onClick={() => {
              setOpenInvoiceModal(true);
              setSelectedOrderSummary(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div className={'flex flex-wrap gap-2 items-center mt-3 w-full mb-5'}>
        <Select
          placeholder={'Select User'}
          className={'w-48'}
          allowClear
          showSearch
          optionFilterProp={'children'}
          value={selectedUser}
          onChange={value => {
            setSelectedUser(value);
            setCurrentPage(1);
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {uniqueUsers.filter(Boolean).map(user => (
            <Option key={user.id} value={user.id}>
              {user.firstName} {user.lastName}
            </Option>
          ))}
        </Select>

        <Select
          placeholder={'Select Product'}
          className={'w-48'}
          allowClear
          showSearch
          optionFilterProp={'children'}
          value={products}
          onChange={value => {
            setProducts(value);
            setCurrentPage(1);
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {uniqueProducts.filter(Boolean).map(product => (
            <Option key={product.productId} value={product.productId}>
              {product.productInfo?.productName || 'Unknown Product'}
            </Option>
          ))}
        </Select>

        <Select
          placeholder={'Select Status'}
          className={'w-48'}
          allowClear
          value={selectedStatus}
          onChange={value => setSelectedStatus(value)}
        >
          <Option key={'CONFIRMED'} value={'CONFIRMED'}>
            Confirmed
          </Option>
          <Option key={'PENDING'} value={'PENDING'}>
            Pending
          </Option>
          <Option key={'CANCELLED'} value={'CANCELLED'}>
            Cancelled
          </Option>
          <Option key={'SHIPPED'} value={'SHIPPED'}>
            Shipped
          </Option>
          <Option key={'DELIVERED'} value={'DELIVERED'}>
            Delivered
          </Option>
        </Select>
        <Button
          className={'w-24 !bg-primary-900 !text-white'}
          type={'primary'}
          onClick={handleResetFilters}
        >
          Reset
        </Button>
      </div>
      <Table
        columns={columns}
        expandable={{expandedRowRender, expandIconColumnIndex: 1}}
        dataSource={paginatedData}
        size={'middle'}
        scroll={{x: 'max-content'}}
        rowKey={row => row.id}
        pagination={false}
      />
      <div className={'flex justify-between items-center mt-4 px-2'}>
        <Select
          value={String(pageSize)}
          onChange={value => {
            setPageSize(Number(value));
            setCurrentPage(1);
          }}
          style={{width: 120}}
        >
          {pageSizeOptions.map(size => (
            <Option key={size} value={size}>
              {size} / page
            </Option>
          ))}
        </Select>

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={tableData.length}
          showSizeChanger={false}
          onChange={page => setCurrentPage(page)}
        />
        <Modal
          title={'Invoice'}
          open={openInvoiceModal}
          width={1200}
          closable
          onCancel={() => setOpenInvoiceModal(false)}
          centered
          cancelText={'Cancel'}
        >
          <InvoiceScreen
            selectedOrderSummary={selectedOrderSummary}
            setSelectedOrderSummary={setSelectedOrderSummary}
            setOpenInvoiceModal={setOpenInvoiceModal}
            categoryList={categoryList}
          />
        </Modal>
      </div>
    </div>
  );
};

export default UserHistory;
